import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { adminSarHelpApi } from '../../lib/api';
import { SARStatusPill } from '../../components/StatusPills';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const fmt = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');

const ThreadEntry = ({ e }) => (
  <div className="rounded-lg bg-ground p-4" data-testid="sar-help-thread-entry">
    <div className="flex items-center justify-between gap-3 flex-wrap">
      <p className="text-sm font-semibold text-ink">
        {e.direction === 'incoming' ? (e.from_address || 'them') : (e.from_name || 'Precious Data')}
        <span className="text-ink-55 font-normal"> → {e.to_address}</span>
      </p>
      <span className="text-xs text-ink-55">{fmt(e.sent_at || e.received_at || e.created_at)}</span>
    </div>
    {e.subject && <p className="text-sm text-ink-70 mt-1">{e.subject}</p>}
    {e.ai_classification && (
      <p className="text-xs text-ink-55 mt-1">Classified as: {e.ai_classification}</p>
    )}
    {e.sealed_to_user ? (
      <p className="text-sm text-ink-55 mt-2" data-testid="sar-help-thread-sealed">
        Sealed to the user. This request has settled, so only they can open what was said.
      </p>
    ) : (
      <p className="text-sm text-ink mt-2 whitespace-pre-wrap">{e.body || '(no body captured)'}</p>
    )}
  </div>
);

/**
 * Detail view for one "Help me with this" flag — everything an admin
 * needs to actually help, not just acknowledge the flag: status, deadline
 * history, and the correspondence while the request is live. Once it has
 * settled the server no longer holds a copy it can open, and entries come
 * back `sealed_to_user` — see admin_sar.py's module docstring.
 */
const AdminSarHelpDetail = () => {
  const { sarId } = useParams();
  const [sar, setSar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resolveOpen, setResolveOpen] = useState(false);
  const [note, setNote] = useState('');
  const [resolving, setResolving] = useState(false);

  const load = () => {
    setLoading(true);
    adminSarHelpApi
      .getOne(sarId)
      .then((res) => setSar(res.data))
      .catch(() => toast.error('Could not load this request.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [sarId]); // eslint-disable-line react-hooks/exhaustive-deps

  const resolve = async () => {
    setResolving(true);
    try {
      await adminSarHelpApi.resolve(sarId, note.trim() || null);
      toast.success('Marked resolved.');
      setResolveOpen(false);
      setNote('');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save that.');
    } finally {
      setResolving(false);
    }
  };

  if (loading) return <p className="text-sm text-ink-55">Loading…</p>;
  if (!sar) return <p className="text-sm text-ink-55">Not found.</p>;

  const thread = [...(sar.email_thread || [])].sort(
    (a, b) => new Date(a.sent_at || a.received_at || a.created_at || 0) - new Date(b.sent_at || b.received_at || b.created_at || 0)
  );
  const alreadyResolved = !!sar.help_resolved_at;

  return (
    <div className="space-y-6" data-testid="admin-sar-help-detail">
      <Link to="/admin/sar-help" className="inline-flex items-center gap-1.5 text-sm font-semibold text-ink-70 hover:text-ink">
        <ArrowLeft className="w-4 h-4" aria-hidden="true" /> SAR help queue
      </Link>

      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-[28px] font-bold tracking-[-0.03em] text-ink">
            {sar.organisation_name}
            {sar.kind !== 'initial' && <span className="text-ink-55 font-normal"> (follow-up)</span>}
          </h1>
          <p className="text-ink-70 mt-1">{sar.user?.full_name} · {sar.user?.email}</p>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <SARStatusPill status={sar.status} daysRemaining={sar.days_remaining} />
          {!alreadyResolved && (
            <Button onClick={() => setResolveOpen(true)} data-testid="sar-help-resolve-btn">
              Mark resolved
            </Button>
          )}
        </div>
      </div>

      {alreadyResolved && (
        <div className="rounded-lg bg-brand-wash px-5 py-4" data-testid="sar-help-resolved-banner">
          <p className="text-[15px] font-semibold text-brand-ink">Resolved {fmt(sar.help_resolved_at)}</p>
          {sar.help_resolved_note && <p className="text-sm text-brand-ink/80 mt-1">{sar.help_resolved_note}</p>}
        </div>
      )}

      {sar.help_reason && (
        <div className="rounded-lg bg-amber-wash px-5 py-4">
          <p className="text-[15px] font-semibold text-amber-ink">What they said</p>
          <p className="text-sm text-ink-70 mt-1">{sar.help_reason}</p>
        </div>
      )}

      <div className="rounded-lg bg-ground px-5 py-4 grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
        <div><p className="text-ink-55">Sent</p><p className="text-ink font-medium">{fmt(sar.sent_at)}</p></div>
        <div><p className="text-ink-55">Deadline</p><p className="text-ink font-medium">{fmt(sar.deadline?.extended_deadline || sar.deadline?.initial_deadline)}</p></div>
        <div><p className="text-ink-55">Documents received</p><p className="text-ink font-medium">{sar.document_count}</p></div>
        <div><p className="text-ink-55">Organisation email</p><p className="text-ink font-medium truncate">{sar.organisation_email || '—'}</p></div>
      </div>

      <div>
        <p className="pd-eyebrow mb-3">Correspondence ({thread.length})</p>
        {thread.length === 0 ? (
          <p className="text-sm text-ink-55">No messages yet.</p>
        ) : (
          <div className="space-y-3">
            {thread.map((e) => <ThreadEntry key={e.id} e={e} />)}
          </div>
        )}
      </div>

      <Dialog open={resolveOpen} onOpenChange={setResolveOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mark this help request resolved</DialogTitle>
            <DialogDescription>
              A note here is just for other admins — it isn't sent to the user.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            placeholder="What did you do to help?"
            className="bg-surface border-surface"
            data-testid="sar-help-resolve-note"
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setResolveOpen(false)} disabled={resolving}>Cancel</Button>
            <Button onClick={resolve} disabled={resolving} data-testid="sar-help-resolve-confirm">
              {resolving && <Loader2 className="w-4 h-4 animate-spin mr-2" aria-hidden="true" />}
              Mark resolved
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminSarHelpDetail;
